import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { LifecyclePhase } from './phases'

export interface ChecklistItem {
  id: string
  title: string
  description: string | null
  ownerRole: string | null
  requiresNote: boolean
  requiresAttachment: boolean
  sortOrder: number
}

export interface ChecklistSection {
  id: string
  title: string
  phase: LifecyclePhase | null
  effectivePhase: LifecyclePhase
  effectivePhaseDisplayName: string
  overridesTemplatePhase: boolean
  sortOrder: number
  items: ChecklistItem[]
}

export interface ChecklistTemplateListItem {
  id: string
  familyId: string
  name: string
  description: string | null
  defaultPhase: LifecyclePhase
  defaultPhaseDisplayName: string
  version: number
  status: 'Draft' | 'Published'
  sectionCount: number
  itemCount: number
  isRetired: boolean
  hasPublishedVersion: boolean
  latestPublishedVersion: number | null
  /** The version a plan is built from, which is not the row's own id once a draft is open. */
  latestPublishedVersionId: string | null
  isUsedByAPlan: boolean
}

export interface ChecklistTemplateDetail {
  id: string
  familyId: string
  name: string
  description: string | null
  defaultPhase: LifecyclePhase
  defaultPhaseDisplayName: string
  version: number
  status: 'Draft' | 'Published'
  isRetired: boolean
  isUsedByAPlan: boolean
  sections: ChecklistSection[]
}

export interface ChecklistItemInput {
  title: string
  description: string | null
  ownerRole: string | null
  requiresNote: boolean
  requiresAttachment: boolean
}

const root = '/api/checklist-templates'
const listKey = ['checklist-templates'] as const
const detailKey = (id: string) => ['checklist-templates', id] as const

export function useChecklistTemplates() {
  return useQuery({
    queryKey: listKey,
    queryFn: () => apiFetch<ChecklistTemplateListItem[]>(root),
  })
}

export function useChecklistTemplate(id: string | undefined) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => apiFetch<ChecklistTemplateDetail>(`${root}/${id}`),
    enabled: Boolean(id),
  })
}

function useChecklistMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey }),
  })
}

export function useCreateChecklistTemplate() {
  return useChecklistMutation(
    (body: { name: string; description: string | null; defaultPhase: LifecyclePhase }) =>
      apiFetch<ChecklistTemplateDetail>(root, { method: 'POST', body }),
  )
}

export function useUpdateChecklistTemplate(id: string) {
  return useChecklistMutation(
    (body: { name: string; description: string | null; defaultPhase: LifecyclePhase }) =>
      apiFetch<ChecklistTemplateDetail>(`${root}/${id}`, { method: 'PUT', body }),
  )
}

export function usePublishChecklistTemplate(id: string) {
  return useChecklistMutation(() =>
    apiFetch<ChecklistTemplateDetail>(`${root}/${id}/publish`, { method: 'POST' }),
  )
}

export function useDeleteChecklistTemplate(id: string) {
  return useChecklistMutation(() => apiFetch<void>(`${root}/${id}`, { method: 'DELETE' }))
}

export function useRetireChecklistTemplate(id: string) {
  return useChecklistMutation(() => apiFetch<void>(`${root}/${id}/retire`, { method: 'POST' }))
}

export function useAddSection(templateId: string) {
  return useChecklistMutation((body: { title: string; phase: LifecyclePhase | null }) =>
    apiFetch<ChecklistSection>(`${root}/${templateId}/sections`, { method: 'POST', body }),
  )
}

export function useDeleteSection(templateId: string) {
  return useChecklistMutation((sectionId: string) =>
    apiFetch<void>(`${root}/${templateId}/sections/${sectionId}`, { method: 'DELETE' }),
  )
}

export function useReorderSections(templateId: string) {
  return useChecklistMutation((ids: string[]) =>
    apiFetch<ChecklistSection[]>(`${root}/${templateId}/sections/order`, {
      method: 'PUT',
      body: { ids },
    }),
  )
}

export function useAddItem(templateId: string) {
  return useChecklistMutation(
    ({ sectionId, ...body }: ChecklistItemInput & { sectionId: string }) =>
      apiFetch<ChecklistItem>(`${root}/${templateId}/sections/${sectionId}/items`, {
        method: 'POST',
        body,
      }),
  )
}

export function useUpdateItem(templateId: string) {
  return useChecklistMutation(
    ({ sectionId, id, ...body }: ChecklistItemInput & { sectionId: string; id: string }) =>
      apiFetch<ChecklistItem>(`${root}/${templateId}/sections/${sectionId}/items/${id}`, {
        method: 'PUT',
        body,
      }),
  )
}

export function useDeleteItem(templateId: string) {
  return useChecklistMutation(({ sectionId, id }: { sectionId: string; id: string }) =>
    apiFetch<void>(`${root}/${templateId}/sections/${sectionId}/items/${id}`, { method: 'DELETE' }),
  )
}

export function useReorderItems(templateId: string) {
  return useChecklistMutation(({ sectionId, ids }: { sectionId: string; ids: string[] }) =>
    apiFetch<ChecklistSection>(`${root}/${templateId}/sections/${sectionId}/items/order`, {
      method: 'PUT',
      body: { ids },
    }),
  )
}

/**
 * Moves one entry of a list, returning the new id order.
 *
 * The server requires a complete ordering, so a move always sends every id
 * rather than a delta - a partial list would leave the rest somewhere nobody chose.
 */
export function moveInOrder<T extends { id: string }>(
  entries: T[],
  id: string,
  direction: -1 | 1,
): string[] {
  const index = entries.findIndex((entry) => entry.id === id)
  const target = index + direction

  if (index < 0 || target < 0 || target >= entries.length) {
    return entries.map((entry) => entry.id)
  }

  const reordered = [...entries]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(target, 0, moved)

  return reordered.map((entry) => entry.id)
}
